import React, { useState } from "react";
import { usePopper } from "react-popper";
import { Popover } from "@headlessui/react";
import { RotateSpinner } from "react-spinners-kit";
import ListSelect from "../ListSelect/ListSelect";
import { ButtonProps, PopOver } from "./interfaces";

const Button: React.FC<ButtonProps> = (props) => {
  const {
    actions,
    actionsMinWidth,
    className,
    disabled,
    full,
    handleActions,
    hideText = false,
    icon: Icon,
    isProducts = false,
    label,
    loading,
    onClick,
    onlyIcon,
    submit = false,
    type = "primary",
    variant = "fill",
    ...remainingProps
  } = props;
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-end",
  });

  const iconColors = {
    primary: variant === "fill" ? "#FFFFFF" : "#D7015A",
    secondary: "#111827",
    danger: variant === "fill" ? "#FFFFFF" : "#DC2626",
    success: variant === "fill" ? "#FFFFFF" : "#52BC55",
    tertiary: variant === "fill" ? "#FFFFFF" : "#000000",
  };

  return (
    <Popover className={full ? "relative w-full" : "relative"}>
      {({ open, close }: PopOver) => (
        <>
          <Popover.Button
            as="div"
            ref={setReferenceElement}
            className={full ? "w-full outline-none" : "outline-none"}
          >
            <button
              type={submit ? "submit" : "button"}
              disabled={disabled}
              onClick={onClick}
              className={`
                min-h-[41px] min-w-fit rounded-md text-sm font-medium transition-colors duration-300 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                  isProducts ? "px-3 py-2" : "px-4 py-2"
                }
                ${full ? "w-full" : ""}
                ${
                  type === "primary" && variant === "fill"
                    ? "bg-primary text-white hover:bg-primary-hover active:bg-primary"
                    : type === "primary" && variant === "outline"
                      ? "border border-gray-200 text-primary hover:bg-primary/10 active:bg-primary/20"
                      : ""
                }
                ${
                  type === "secondary"
                    ? "border border-gray-200 bg-white text-black hover:bg-gray-100 active:bg-gray-100"
                    : ""
                }
                ${
                  type === "success" && variant === "fill"
                    ? "bg-success text-white hover:bg-success-hover active:bg-success"
                    : type === "success" && variant === "outline"
                      ? "border border-gray-200 text-success hover:bg-success/10 active:bg-success/20"
                      : ""
                }
                ${
                  type === "danger" && variant === "fill"
                    ? "bg-red-600 text-white hover:bg-red-700 active:bg-red-600"
                    : type === "danger" && variant === "outline"
                      ? "border border-gray-200 text-red-600 hover:bg-red-600/10 active:bg-red-600/20"
                      : ""
                }
                ${
                  type === "tertiary" && variant === "fill"
                    ? "bg-black text-white hover:bg-gray-800 active:bg-black disabled:bg-gray-200"
                    : type === "tertiary" && variant === "outline"
                      ? "border border-gray-200 text-black hover:bg-black/10 active:bg-black/20"
                      : ""
                }
                ${className || ""}
              `}
              {...remainingProps}
            >
              <div
                className={`flex items-center justify-center ${
                  label !== "" && "gap-2"
                }`}
              >
                {loading && (
                  <div className="absolute">
                    <RotateSpinner
                      size={20}
                      color={iconColors[type]}
                      loading={loading}
                    />
                  </div>
                )}
                {Icon && (
                  <Icon
                    className={`h-4 w-4 shrink-0 stroke-2 ${
                      loading ? "opacity-0" : ""
                    }`}
                  />
                )}
                {!hideText && (
                  <span
                    className={`${loading && "opacity-0"} ${
                      onlyIcon && "hidden md:block"
                    }`}
                  >
                    {label}
                  </span>
                )}
              </div>
            </button>
          </Popover.Button>
          {actions && (
            <Popover.Panel
              ref={setPopperElement}
              style={styles.popper}
              {...attributes.popper}
              className="z-[1000] w-screen max-w-max"
              onClick={close}
            >
              <ListSelect
                items={actions}
                minWidth={actionsMinWidth}
                onChange={handleActions}
                open={open && actions.length > 0}
              />
            </Popover.Panel>
          )}
        </>
      )}
    </Popover>
  );
};

export default Button;
